import React, { useEffect, useRef, useState } from 'react';
import {
  Play,
  Square,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  X,
  Save,
  ChevronRight,
  Info,
} from 'lucide-react';
import api from '../api';

interface TestCase {
  id: string;
  module: string;
  description: string | null;
  tags: string[];
  expected_claims_count: number;
  forbidden_claims_count: number;
  required_fields_count: number;
  valid: boolean;
  validation_errors: string[];
}

interface CaseResult {
  id: string;
  module: string;
  passed?: boolean;
  overall_score?: number;
  latency_ms?: number;
  error?: string | null;
  expected?: { match_rate?: number; critical_match_rate?: number; missing?: string[] };
  forbidden?: { hit_rate?: number; hit_phrases?: string[] };
  structural?: { completeness?: number; missing_fields?: string[] };
  counts?: { issues?: string[] };
}

interface Summary {
  total_cases: number;
  passed: number;
  pass_rate: number;
  avg_match_rate: number;
  avg_critical_match_rate: number;
  avg_forbidden_hit_rate: number;
  avg_structural_completeness: number;
  avg_overall_score: number;
  avg_latency_ms: number;
}

interface RunState {
  activeRunId: string | null;
  status: 'idle' | 'running' | 'completed' | 'cancelled' | 'error';
  startedAt: string | null;
  finishedAt: string | null;
  totalCases: number;
  completedCases: number;
  currentCaseId: string | null;
  currentCaseModule: string | null;
  moduleFilter: string | null;
  dryRun: boolean;
  error: string | null;
  summary: Summary | null;
  results: CaseResult[];
  savedBaseline?: string | null;
}

interface Baseline {
  filename: string;
  label?: string;
  timestamp?: string;
  summary?: Summary;
}

const MODULE_LABELS: Record<string, string> = {
  rcsa_analyzer: 'RCSA Analyzer',
  process_mapper: 'Process Mapper',
  migration_architect: 'Migration Architect',
  hallucination_trap: 'Hallucination Trap'
};

const MODULE_COLORS: Record<string, string> = {
  rcsa_analyzer: 'bg-blue-100 text-blue-800',
  process_mapper: 'bg-green-100 text-green-800',
  migration_architect: 'bg-purple-100 text-purple-800',
  hallucination_trap: 'bg-orange-100 text-orange-800'
};

const AccuracyTestSuite: React.FC = () => {
  const [cases, setCases] = useState<TestCase[]>([]);
  const [byModule, setByModule] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [runState, setRunState] = useState<RunState | null>(null);
  const [baselines, setBaselines] = useState<Baseline[]>([]);

  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [caseFilter, setCaseFilter] = useState<string>('');
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [saveAsBaseline, setSaveAsBaseline] = useState<boolean>(false);
  const [baselineLabel, setBaselineLabel] = useState<string>('baseline');

  const [expandedResult, setExpandedResult] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [selectedBaseline, setSelectedBaseline] = useState<any | null>(null);

  const pollRef = useRef<any>(null);

  const loadCases = async () => {
    try {
      const res = await api.get('/api/accuracy-test-suite/cases');
      setCases(res.data.cases || []);
      setByModule(res.data.by_module || {});
    } catch (err) {
      console.error('Failed to load test cases:', err);
    }
  };

  const loadBaselines = async () => {
    try {
      const res = await api.get('/api/accuracy-test-suite/baselines');
      setBaselines(res.data.baselines || []);
    } catch (err) {
      console.error('Failed to load baselines:', err);
    }
  };

  const loadStatus = async () => {
    try {
      const res = await api.get('/api/accuracy-test-suite/status');
      setRunState(res.data.state);
      if (res.data.state.status !== 'running' && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        loadBaselines();
      }
    } catch (err) {
      console.error('Failed to load status:', err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadCases(), loadStatus(), loadBaselines()]);
      setLoading(false);
    })();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (runState?.status === 'running' && !pollRef.current) {
      pollRef.current = setInterval(loadStatus, 2000);
    }
  }, [runState?.status]);

  const startRun = async () => {
    try {
      const body: any = {};
      if (moduleFilter !== 'all') body.module = moduleFilter;
      if (caseFilter.trim()) body.case_id = caseFilter.trim();
      if (dryRun) body.dry_run = true;
      if (saveAsBaseline && !dryRun) body.save_label = baselineLabel || 'baseline';

      await api.post('/api/accuracy-test-suite/run', body);
      setExpandedResult(null);
      await loadStatus();
      if (!pollRef.current) {
        pollRef.current = setInterval(loadStatus, 2000);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to start run';
      alert(msg);
    }
  };

  const cancelRun = async () => {
    try {
      await api.post('/api/accuracy-test-suite/cancel');
      await loadStatus();
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  };

  const loadCaseDetail = async (caseId: string) => {
    try {
      const res = await api.get(`/api/accuracy-test-suite/cases/${caseId}`);
      setSelectedCase(res.data.case);
    } catch (err) {
      console.error('Failed to load case detail:', err);
    }
  };

  const loadBaselineDetail = async (filename: string) => {
    try {
      const res = await api.get(`/api/accuracy-test-suite/baselines/${filename}`);
      setSelectedBaseline(res.data.baseline);
    } catch (err) {
      console.error('Failed to load baseline detail:', err);
    }
  };

  const isRunning = runState?.status === 'running';
  const progress = runState && runState.totalCases > 0
    ? Math.round((runState.completedCases / runState.totalCases) * 100)
    : 0;

  const resultsByStatus = {
    passed: runState?.results.filter(r => r.passed).length || 0,
    failed: runState?.results.filter(r => !r.passed && !r.error).length || 0,
    errored: runState?.results.filter(r => r.error).length || 0
  };

  const fmtPct = (n: number | undefined) => n === undefined || n === null ? '—' : `${(n * 100).toFixed(1)}%`;
  const fmtScore = (n: number | undefined) => n === undefined || n === null ? '—' : n.toFixed(1);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" />
            Accuracy Test Suite
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Golden test cases for RCSA, Process Mapper, Migration Architect, and hallucination traps. Run against current Jarvis modules and measure regression over time.
          </p>
        </div>
        <button
          onClick={() => { loadCases(); loadStatus(); loadBaselines(); }}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Post-Change Accuracy Protocol — Section 06 */}
      <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-indigo-200">
          <Info className="w-4 h-4 text-indigo-600 shrink-0" />
          <span className="text-xs font-bold text-indigo-800 uppercase tracking-wide">Post-Change Accuracy Protocol</span>
          <span className="text-[10px] text-indigo-500 ml-1">· Run after every model switch or significant prompt change</span>
        </div>
        <div className="px-4 py-3">
          <div className="flex flex-wrap gap-1 items-center mb-3">
            {[
              { step: '1', label: 'Filter to affected module' },
              { step: '2', label: 'Run Now (Dry Run first)' },
              { step: '3', label: 'Review escalations in Verification Dashboard (target: failure rate < 30%)' },
              { step: '4', label: 'Update thresholds in Verification Config if ECE gap > 10pp' },
              { step: '5', label: 'Check Failure Patterns (E-6) in Verification Feedback for systematic claim-type issues' },
            ].map((s, i, arr) => (
              <React.Fragment key={s.step}>
                <div className="flex items-center gap-1.5 bg-white border border-indigo-200 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{s.step}</span>
                  <span className="text-[11px] text-indigo-900 font-medium leading-snug max-w-[160px]">{s.label}</span>
                </div>
                {i < arr.length - 1 && <ChevronRight className="w-3 h-3 text-indigo-300 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
          <p className="text-[11px] text-indigo-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
            <span className="font-bold">Do not mark a change stable until reviewed.</span> Current worst failure rates: SOP Generation Enhanced 58% low-confidence · Process Map Level 3 71% low-confidence — prompt engineering needed before these are considered stable.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase">Total Cases</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{cases.length}</div>
        </div>
        {Object.entries(byModule).map(([mod, count]) => (
          <div key={mod} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase truncate">{MODULE_LABELS[mod] || mod}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{count}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Play className="w-4 h-4" />
          Run Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Module Filter</label>
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              disabled={isRunning}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            >
              <option value="all">All Modules</option>
              {Object.keys(byModule).map(m => (
                <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Specific Case ID (optional)</label>
            <input
              type="text"
              value={caseFilter}
              onChange={(e) => setCaseFilter(e.target.value)}
              placeholder="e.g. rcsa-001"
              disabled={isRunning}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            />
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={isRunning}
                className="rounded border-gray-300"
              />
              <span>Dry Run (no API calls)</span>
            </label>
          </div>
          <div className="flex items-end gap-2">
            {!isRunning ? (
              <button
                onClick={startRun}
                className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
              >
                <Play className="w-4 h-4" />
                Run Now
              </button>
            ) : (
              <button
                onClick={cancelRun}
                className="flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                <Square className="w-4 h-4" />
                Cancel
              </button>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 pt-3 border-t border-gray-100">
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={saveAsBaseline}
              onChange={(e) => setSaveAsBaseline(e.target.checked)}
              disabled={isRunning || dryRun}
              className="rounded border-gray-300"
            />
            <Save className="w-3.5 h-3.5 text-gray-500" />
            <span>Save as baseline</span>
          </label>
          {saveAsBaseline && (
            <input
              type="text"
              value={baselineLabel}
              onChange={(e) => setBaselineLabel(e.target.value)}
              placeholder="baseline label"
              disabled={isRunning}
              className="px-2 py-1 text-xs border border-gray-300 rounded w-48"
            />
          )}
          {dryRun && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
              Dry run doesn't save baselines
            </span>
          )}
        </div>
      </div>

      {runState && (runState.status === 'running' || runState.status === 'completed' || runState.status === 'error') && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-900">
                {runState.status === 'running' ? 'Running…' : runState.status === 'completed' ? 'Completed' : 'Error'}
              </span>
              <span className="text-xs text-gray-500">
                {runState.completedCases} / {runState.totalCases} cases
              </span>
              {runState.dryRun && <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">dry run</span>}
              {runState.savedBaseline && (
                <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                  saved → {runState.savedBaseline}
                </span>
              )}
            </div>
            {runState.currentCaseId && runState.status === 'running' && (
              <span className="text-xs text-gray-600">
                Now: {runState.currentCaseId} ({MODULE_LABELS[runState.currentCaseModule || ''] || runState.currentCaseModule})
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-4">
            <div
              className="h-2 bg-indigo-600 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>

          {runState.status === 'error' && runState.error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 mb-3">
              {runState.error}
            </div>
          )}

          {runState.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div>
                <div className="text-xs text-gray-500">Pass Rate</div>
                <div className="text-xl font-bold text-gray-900">{fmtPct(runState.summary.pass_rate)}</div>
                <div className="text-[10px] text-gray-400">{runState.summary.passed}/{runState.summary.total_cases}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Avg Overall Score</div>
                <div className="text-xl font-bold text-indigo-700">{fmtScore(runState.summary.avg_overall_score)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Avg Match Rate</div>
                <div className="text-xl font-bold text-green-700">{fmtPct(runState.summary.avg_match_rate)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Avg Hallucination Rate</div>
                <div className="text-xl font-bold text-red-700">{fmtPct(runState.summary.avg_forbidden_hit_rate)}</div>
              </div>
            </div>
          )}

          {runState.results.length > 0 && (
            <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> {resultsByStatus.passed} passed</span>
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> {resultsByStatus.failed} failed</span>
              <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-600" /> {resultsByStatus.errored} errored</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> avg latency {runState.summary ? runState.summary.avg_latency_ms : 0}ms</span>
            </div>
          )}

          {runState.results.length > 0 && (
            <div className="border border-gray-200 rounded max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3">Case</th>
                    <th className="text-left py-2 px-3">Module</th>
                    <th className="text-center py-2 px-3">Status</th>
                    <th className="text-right py-2 px-3">Score</th>
                    <th className="text-right py-2 px-3">Match</th>
                    <th className="text-right py-2 px-3">Latency</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {runState.results.map((r, i) => {
                    const expanded = expandedResult === r.id;
                    return (
                      <React.Fragment key={i}>
                        <tr className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-mono text-[11px]">{r.id}</td>
                          <td className="py-2 px-3">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${MODULE_COLORS[r.module] || 'bg-gray-100 text-gray-700'}`}>
                              {MODULE_LABELS[r.module] || r.module}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {r.error ? (
                              <XCircle className="w-4 h-4 text-red-600 inline" />
                            ) : r.passed ? (
                              <CheckCircle className="w-4 h-4 text-green-600 inline" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-amber-600 inline" />
                            )}
                          </td>
                          <td className="py-2 px-3 text-right font-medium">{fmtScore(r.overall_score)}</td>
                          <td className="py-2 px-3 text-right">{fmtPct(r.expected?.match_rate)}</td>
                          <td className="py-2 px-3 text-right text-gray-500">{r.latency_ms}ms</td>
                          <td className="py-2 px-3 text-right">
                            <button
                              onClick={() => setExpandedResult(expanded ? null : r.id)}
                              className="text-gray-400 hover:text-gray-700"
                            >
                              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="bg-gray-50 border-t border-gray-100">
                            <td colSpan={7} className="p-3">
                              <div className="text-xs space-y-1">
                                {r.error && <div className="text-red-700"><strong>Error:</strong> {r.error}</div>}
                                {r.expected?.missing && r.expected.missing.length > 0 && (
                                  <div><strong>Missing expected claims:</strong> {r.expected.missing.join(', ')}</div>
                                )}
                                {r.forbidden?.hit_phrases && r.forbidden.hit_phrases.length > 0 && (
                                  <div className="text-red-700"><strong>⚠️ Hallucinations detected:</strong> {r.forbidden.hit_phrases.join(', ')}</div>
                                )}
                                {r.structural?.missing_fields && r.structural.missing_fields.length > 0 && (
                                  <div><strong>Missing structural fields:</strong> {r.structural.missing_fields.join(', ')}</div>
                                )}
                                {r.counts?.issues && r.counts.issues.length > 0 && (
                                  <div><strong>Count issues:</strong> {r.counts.issues.join('; ')}</div>
                                )}
                                <button
                                  onClick={() => loadCaseDetail(r.id)}
                                  className="text-indigo-600 hover:underline mt-2"
                                >
                                  View full case definition →
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Test Case Library
          </h3>
          <span className="text-xs text-gray-500">{cases.length} cases</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase sticky top-0">
              <tr>
                <th className="text-left py-2 px-3">ID</th>
                <th className="text-left py-2 px-3">Module</th>
                <th className="text-left py-2 px-3">Description</th>
                <th className="text-center py-2 px-3">Expected</th>
                <th className="text-center py-2 px-3">Forbidden</th>
                <th className="text-center py-2 px-3">Valid</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => loadCaseDetail(c.id)}
                >
                  <td className="py-2 px-3 font-mono text-[11px]">{c.id}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${MODULE_COLORS[c.module] || 'bg-gray-100 text-gray-700'}`}>
                      {MODULE_LABELS[c.module] || c.module}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-700 truncate max-w-md">{c.description || '—'}</td>
                  <td className="py-2 px-3 text-center">{c.expected_claims_count}</td>
                  <td className="py-2 px-3 text-center">{c.forbidden_claims_count}</td>
                  <td className="py-2 px-3 text-center">
                    {c.valid ? <CheckCircle className="w-3.5 h-3.5 text-green-600 inline" /> : <XCircle className="w-3.5 h-3.5 text-red-600 inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Save className="w-4 h-4" />
            Saved Baselines
          </h3>
          <span className="text-xs text-gray-500">{baselines.length} baseline(s)</span>
        </div>
        {baselines.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            No baselines saved yet. Enable "Save as baseline" before running to capture a snapshot.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {baselines.map((b) => (
              <li
                key={b.filename}
                className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                onClick={() => loadBaselineDetail(b.filename)}
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">{b.label || '(no label)'}</div>
                  <div className="text-xs text-gray-500 font-mono">{b.filename}</div>
                </div>
                <div className="text-right text-xs text-gray-600">
                  {b.timestamp && <div>{new Date(b.timestamp).toLocaleString()}</div>}
                  {b.summary && (
                    <div className="flex items-center gap-3 mt-1">
                      <span>{b.summary.passed}/{b.summary.total_cases} passed</span>
                      <span>score {fmtScore(b.summary.avg_overall_score)}</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{selectedCase.id}</h3>
              <button onClick={() => setSelectedCase(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <pre className="text-xs font-mono bg-gray-50 p-3 rounded overflow-x-auto">
                {JSON.stringify(selectedCase, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {selectedBaseline && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Baseline: {selectedBaseline.label}</h3>
                <p className="text-xs text-gray-500">{selectedBaseline.timestamp}</p>
              </div>
              <button onClick={() => setSelectedBaseline(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {selectedBaseline.summary && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div>
                    <div className="text-xs text-gray-500">Pass Rate</div>
                    <div className="text-lg font-bold">{fmtPct(selectedBaseline.summary.pass_rate)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Avg Score</div>
                    <div className="text-lg font-bold">{fmtScore(selectedBaseline.summary.avg_overall_score)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Avg Match</div>
                    <div className="text-lg font-bold">{fmtPct(selectedBaseline.summary.avg_match_rate)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Avg Hallucination</div>
                    <div className="text-lg font-bold">{fmtPct(selectedBaseline.summary.avg_forbidden_hit_rate)}</div>
                  </div>
                </div>
              )}
              <table className="w-full text-xs">
                <thead className="text-gray-500 border-b">
                  <tr>
                    <th className="text-left py-1">Case</th>
                    <th className="text-left py-1">Module</th>
                    <th className="text-center py-1">Passed</th>
                    <th className="text-right py-1">Score</th>
                    <th className="text-right py-1">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedBaseline.results || []).map((r: CaseResult, i: number) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-1 font-mono text-[11px]">{r.id}</td>
                      <td className="py-1">{MODULE_LABELS[r.module] || r.module}</td>
                      <td className="py-1 text-center">{r.passed ? '✓' : '✗'}</td>
                      <td className="py-1 text-right">{fmtScore(r.overall_score)}</td>
                      <td className="py-1 text-right">{fmtPct(r.expected?.match_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccuracyTestSuite;
